import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Flame, CalendarDays, Users, Activity, ShieldCheck, LogOut, User as UserIcon, Dumbbell, Bell, Trophy, ListChecks } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { mediaUrl, notificationsAPI } from '../services/api';

const memberItems = [
  { to: '/app', label: "Aujourd'hui", icon: Flame, testId: 'nav-item-today' },
  { to: '/parcours', label: '21 Jours', icon: CalendarDays, testId: 'nav-item-calendar' },
  { to: '/defis', label: 'Défis', icon: ListChecks, testId: 'nav-item-defis' },
  { to: '/classement', label: 'Classement', icon: Trophy, testId: 'nav-item-leaderboard' },
  { to: '/communaute', label: 'Communauté', icon: Users, testId: 'nav-item-community' },
  { to: '/progres', label: 'Progrès', icon: Activity, testId: 'nav-item-progress' },
];

const coachItems = [
  { to: '/app', label: "Aujourd'hui", icon: Flame, testId: 'nav-item-today' },
  { to: '/parcours', label: '21 Jours', icon: CalendarDays, testId: 'nav-item-calendar' },
  { to: '/classement', label: 'Classement', icon: Trophy, testId: 'nav-item-leaderboard' },
  { to: '/communaute', label: 'Communauté', icon: Users, testId: 'nav-item-community' },
  { to: '/coach', label: 'Coach', icon: ShieldCheck, testId: 'nav-item-coach' },
];

export const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isCoach, logout } = useAuth();
  const [unread, setUnread] = useState(0);

  const items = isCoach ? coachItems : memberItems;

  const refreshUnread = useCallback(() => {
    notificationsAPI.list().then((d) => setUnread(d.unread_count)).catch(() => {});
  }, []);

  useEffect(() => {
    refreshUnread();
    const id = setInterval(refreshUnread, 60000);
    const onRead = () => setUnread(0);
    window.addEventListener('bfc-notif-read', onRead);
    return () => { clearInterval(id); window.removeEventListener('bfc-notif-read', onRead); };
  }, [refreshUnread, location.pathname]);

  const isActive = (to) => location.pathname === to;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background dark">
      {/* Top header */}
      <header className="sticky top-0 z-40 bfc-dark backdrop-blur-md border-b border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2" data-testid="header-logo">
            <img src="/bfc21-logo.png" alt="Body Fit Challenge 21" className="w-10 h-10 rounded-full" />
            <span className="font-display font-extrabold text-white tracking-tight hidden sm:block text-lg">BODY FIT <span className="text-orange-500">CHALLENGE 21</span></span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`${it.testId}-desktop`}
                className={`px-3 py-2 rounded-full text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors ${
                  isActive(it.to)
                    ? 'bg-orange-500 text-white'
                    : 'text-neutral-300 hover:bg-neutral-800'
                }`}
              >
                <it.icon className="w-4 h-4" />
                {it.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/notifications" data-testid="notifications-bell" className="relative w-9 h-9 rounded-full flex items-center justify-center text-neutral-300 hover:bg-neutral-800">
              <Bell className="w-5 h-5" />
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">{unread > 9 ? '9+' : unread}</span>
              )}
            </Link>
            <Link to="/profil" data-testid="header-profile-link">
              <Avatar className="w-9 h-9 ring-2 ring-neutral-700">
                <AvatarImage src={mediaUrl(user?.avatar)} />
                <AvatarFallback className="bg-red-600 text-white text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || <UserIcon className="w-4 h-4" />}
                </AvatarFallback>
              </Avatar>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-neutral-300 hover:text-white hover:bg-neutral-800 hidden sm:inline-flex"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="bfc-member-bg min-h-[calc(100vh-4rem)]">
        <div className="max-w-5xl mx-auto px-4 pt-5 pb-28 md:pb-10">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0b0b0e]/95 backdrop-blur-md border-t border-neutral-800">
        <div className="max-w-5xl mx-auto px-2 grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              className={`flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-semibold transition-colors ${
                isActive(it.to) ? 'text-orange-500' : 'text-neutral-400'
              }`}
            >
              <it.icon className={`w-5 h-5 ${isActive(it.to) ? 'text-orange-500' : ''}`} />
              {it.label}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
};
